// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef } from 'react';
import { Animated, Dimensions, Easing, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import ScorecardScreen from '../../../round/[roundId]/scorecard';
import appColors, { alpha } from '@/theme/colors';

export default function ScorecardSheetScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const windowHeight = Dimensions.get('window').height;
  const translateY = useRef(new Animated.Value(windowHeight)).current;

  useEffect(() => {
    Animated.timing(translateY, {
      toValue: 0,
      duration: 260,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [translateY]);

  const closeSheet = useCallback(() => {
    Animated.timing(translateY, {
      toValue: windowHeight,
      duration: 180,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start(() => {
      router.back();
    });
  }, [router, translateY, windowHeight]);

  return (
    <Pressable style={styles.overlay} onPress={closeSheet}>
      <Animated.View style={[styles.sheet, { transform: [{ translateY }] }]}>
        <View style={styles.header}>
          <Pressable onPress={closeSheet} hitSlop={8}>
            <Text style={styles.backText}>{t('menu.back')}</Text>
          </Pressable>
          <Text style={styles.title}>{t('scorecard.title')}</Text>
          <View style={styles.rightSpacer} />
        </View>
        <Pressable style={styles.content} onPress={() => null}>
          <ScorecardScreen />
        </Pressable>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.ink, 0.45),
    justifyContent: 'flex-end',
  },
  sheet: {
    height: '88%',
    backgroundColor: appColors.neutral.navyDeep,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    borderWidth: 1,
    borderColor: appColors.accent.indigo,
    overflow: 'hidden',
  },
  header: {
    height: 52,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.hairline,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: appColors.accent.tealDeep,
  },
  backText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  title: {
    color: appColors.surface.light,
    fontSize: 16,
    fontWeight: '700',
  },
  rightSpacer: {
    width: 56,
  },
  content: {
    flex: 1,
  },
});
